import React, { useCallback, useMemo, useState } from "react";
import { matchPath, Redirect, useLocation } from "react-router-dom";
import { Route, Switch } from "react-router-dom";

import { Stack } from "@mui/material";
import { RoleComponentGuard } from "components";
import { AccessDenied } from "components/Access-denied/AccessDenied";
import { Correlation, LiquidityGraph, Table, Tabs, TelescopeTable } from "components/Benchmark";
import { Routes } from "constants/routes";
import { useNavigate } from "hooks";
import { useAccountSelector, usePickUserPermissions } from "store";
import { UserPermissionType } from "store/types";

import { RoleName } from "../../constants";

import "./Benchmark.styles.scss";

type BenchmarkTab = "performance" | "liquidity" | "telescope" | "correlation";

export const Benchmark: React.FC = () => {
  const { pathname } = useLocation();

  const enabledTab = useMemo(() => {
    if (matchPath(pathname, Routes["benchmarkLiquidity"])) return "liquidity";
    if (matchPath(pathname, Routes["benchmarkTelescope"])) return "telescope";
    if (matchPath(pathname, Routes["benchmarkCorrelation"])) return "correlation";
    if (matchPath(pathname, Routes["benchmarkPerformance"])) return "performance";
    return "performance";
  }, [pathname]);

  const [tab, setTab] = useState<BenchmarkTab>(enabledTab);
  const navigate = useNavigate();

  const handleTabChange = useCallback(
    (value: BenchmarkTab) => {
      setTab(value);
      if (value === "performance") navigate({ route: "benchmarkPerformance" });
      if (value === "liquidity") navigate({ route: "benchmarkLiquidity" });
      if (value === "telescope") navigate({ route: "benchmarkTelescope" });
      if (value === "correlation") navigate({ route: "benchmarkCorrelation" });
    },
    [navigate]
  );

  const { show_telescope } = useAccountSelector() || {};
  const benchmark = usePickUserPermissions("benchmark");

  const noAccessPermission = benchmark && benchmark === UserPermissionType.NO_ACCESS;

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      hideChild={noAccessPermission}
      RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
    >
      <Stack
        data-testid="benchmark-page page"
        className="benchmark"
        sx={{
          p: "2px",
          width: "100%",
          height: "100%",
          // overflow: "hidden",
          gap: "16px"
        }}
      >
        <Tabs value={tab} onChange={handleTabChange} />
        <Switch>
          <Route exact path={Routes["benchmark"]} render={() => <Redirect to={Routes["benchmarkPerformance"]} />} />
          <Route exact path={Routes["benchmarkPerformance"]} render={() => <Table currency={"USD"} />} />
          <Route
            path={Routes["benchmarkLiquidity"]}
            render={() => (
              <Stack direction="row" gap="40px">
                <LiquidityGraph />
              </Stack>
            )}
          />
          {show_telescope && <Route exact path={Routes["benchmarkTelescope"]} render={() => <TelescopeTable />} />}
          <Route
            exact
            path={[Routes["benchmarkCorrelation"], Routes["benchmarkCorrelationInvestor"]]}
            render={() => <Correlation />}
          />
        </Switch>
      </Stack>
    </RoleComponentGuard>
  );
};
